import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { withLocalePrefix } from 'shared/utils/translation';
import { GLOBAL_HEADER_ID } from 'talent_matching/hooks/use_sticky_grid_header';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';

const t = withLocalePrefix('talent_matching.table.background_ranking');
export const BANNER_ID = 'background-ranking-banner';
export const BANNER_CHANGE_EVENT = 'tm-banner-change';

const StickyWrapper = styled.div<{ $topOffset: number }>`
  position: sticky;
  top: ${({ $topOffset }) => $topOffset}px;
  z-index: 19;
  overflow: hidden;
`;

const useHeaderOffset = () => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const header = document.getElementById(GLOBAL_HEADER_ID);
    if (!header) {
      return;
    }

    const update = () => setOffset(header.getBoundingClientRect().height);
    update();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(header);
      return () => observer.disconnect();
    }
  }, []);

  return offset;
};

type Props = {
  onRefresh: () => void;
};

const BackgroundRankingBanner = ({ onRefresh }: Props) => {
  const { rankingInProgress, rankingCompleted, isBackgroundRanking } =
    useRankingInProgress();
  const topOffset = useHeaderOffset();
  const backgroundRankingInProgress = rankingInProgress && isBackgroundRanking;
  const isVisible = backgroundRankingInProgress || rankingCompleted;

  useEffect(() => {
    // Defer to next frame so the banner has been fully painted before
    // use_sticky_grid_header reads its height via getBoundingClientRect.
    const rafId = requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent(BANNER_CHANGE_EVENT));
    });
    return () => cancelAnimationFrame(rafId);
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <StickyWrapper id={BANNER_ID} $topOffset={topOffset}>
      {backgroundRankingInProgress ? (
        <InformationalCallOut
          bodyText={t('in_progress')}
          data-provides="background-ranking-in-progress"
        />
      ) : (
        <InformationalCallOut
          bodyText={t('completed')}
          buttonProps={{
            children: t('refresh'),
            onClick: onRefresh,
          }}
          data-provides="background-ranking-completed"
        />
      )}
    </StickyWrapper>
  );
};

export default BackgroundRankingBanner;
